'use client';
import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { BaseLineMarket } from '@/app/components/icons/BaseLineMarket';
import { ProductLineMark } from '@/app/components/icons/ProductLineMark';
import { Button } from '@/app/components/ui/button';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch } from '@/app/hooks';
import type { ReportParams } from '@/app/types';
import { cn, reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';
import { favorsDisfavor, favorsFavorite } from './apis';

interface StarButtonProps {
  className?: string;
  id: string;
  starClassName?: string;
  isStarred: boolean;
  onStar?: (isStarred: boolean) => void;

  // 用于上报事件
  reportParams?: ReportParams;
}

export const StarButton: FC<StarButtonProps> = ({ className, id, starClassName, isStarred, onStar, reportParams }) => {
  const t = useTranslations();
  const pathname = usePathname();
  const { loading: favoring, run: updateFavorite } = useFetch(
    () =>
      isStarred
        ? favorsDisfavor({
            path: {
              obj_id: id,
              obj_type: 'creation',
            },
          })
        : favorsFavorite({
            path: {
              obj_id: id,
              obj_type: 'creation',
            },
            body: {
              obj_id: id,
              obj_type: 'creation',
            },
          }),
    {
      manual: true,
      onSuccess: ({ error }) => {
        if (error) {
          toast({
            type: 'warning',
            message: t('popup.toast.operationFailed'),
          });
          return;
        }
        onStar?.(!isStarred);
      },
    },
  );

  return (
    <Button
      variant="text"
      size="original"
      className={className}
      aria-selected={isStarred}
      onClick={() => {
        if (favoring) return;
        updateFavorite();
        reportEvent('operation', {
          interaction_type: isStarred ? 'cancel_star' : 'star',
          interaction_position: pathname,
          task_id: '',
          creation_id: id,
          content: '',
          ...reportParams,
        });
      }}
    >
      {isStarred ? (
        <BaseLineMarket className={cn('text-[#FFCC00]', starClassName)} />
      ) : (
        <ProductLineMark className={starClassName} />
      )}
    </Button>
  );
};
